import React, { Component } from 'react';
import { Button } from 'react-bootstrap';
import 'swiper/css';
import 'swiper/css/pagination';
import { Swiper, SwiperSlide } from 'swiper/react';
import '../../../Choices/research-action.css';
import { GamePlayerStatus, Texture, TurnStatus } from '../../../../../Constants';
import { whichStacksHaveThisAction } from '../../../../Game/GamePlayerInfo';

import blank1080 from '../../../Common/blank-1080x210.png';
import { availableRandomActions } from '../../../Common/StationUtilities';
import { getTextureUrl } from '../../../Common/TextureTools';
import './action-selector.css';
import {
    available_action_hex_actions,
    currentActionGrid,
    factionActionIdForTexture,
    getActionGrid,
    isMainHexActionTexture,
    isRandomActionTexture,
} from '../../../Common/ActionHexUtilities';
import { ASSETS_URL } from '../../../../../Settings';

const COLORS = [
    null, 'var(--deep-faded)', 'var(--stem-faded)', 'var(--hex-faded)', 'var(--dread-faded)', 'var(--honored-faded)', 'var(--seed-faded)'
];
const VARIANTS = [
    null, 'deep', 'stem', 'hex', 'dread', 'honored', 'seed'
];

// Manual §7.1 mid-turn statuses. Inline End Turn is the explicit
// yield-mid-turn affordance: it lets the player force end_turn() while
// they still have queued faction/random actions that the state machine
// won't auto-resolve. Mirrors the gate that lived on the now-removed
// standalone EndTurnButton.
const MID_TURN_STATUSES = new Set([
    TurnStatus.CHOOSE_ACTIONS_ID,
    TurnStatus.SELECT_ACTION_ID,
    TurnStatus.ACTION_COMPLETE_ID,
    TurnStatus.LOCKED_ID,
]);


// Dead-but-load-bearing-for-audit per CC_END_TURN_ANYTIME_PREDICATE
// (no blind deletes — feedback_no_blind_deletes). As of that CC,
// shouldShowInlineEndTurn no longer gates on hex exhaustion (Manual
// §7.1: a seat may end its turn at any point in the Action Phase, and
// the backend next_button already honors an EndTurn click from every
// mid-turn turn_status). This helper is retained for audit/reference;
// render() decides full-region vs --below purely on newActionGrid
// length, which derives from currentActionGrid, not this function.
function isHexExhausted(gamePlayerData) {
    // True when the player has burned through all main-hex action
    // slots. Faction tokens and random actions are tracked separately
    // and can still be queued — the inline End Turn exists precisely
    // for that bank-and-yield case.
    const stacks = available_action_hex_actions(
        gamePlayerData.station, gamePlayerData.robot_assignments,
        gamePlayerData.action_hex_position,
        gamePlayerData.locked_action_stacks,
        gamePlayerData.available_actions);
    return stacks.every((stack) =>
        stack.every((action) => action === null || action === undefined));
}


function shouldShowInlineEndTurn(gamePlayerData, gameData) {
    if (!gamePlayerData || !gameData) return false;
    if (gameData.active_player !== gamePlayerData.id) return false;
    if (gamePlayerData.status !== GamePlayerStatus.TAKING_TURN_ID) {
        return false;
    }
    if (!MID_TURN_STATUSES.has(gamePlayerData.turn_status)) return false;
    return true;
}


export default class ActionSelector extends Component {
    constructor(props) {
        super(props);
        this.state = { tick: 0 };
        this.seatedController = this.props.app.game.seatedController;
        this.api = this.seatedController
            ? this.seatedController.gamePlayerApi : null;
        // Per-seat event name. The seated controller fans out every
        // wire update and every local patch through this channel —
        // we tick on every fire and re-derive the grid from the
        // controller's authoritative gamePlayerData in render() and
        // again, freshly, in onClick. No render-time cache: a stored
        // grid lags the controller by at least one React cycle, and
        // a fast tap or parent re-render would fire the wrong action.
        this.gamePlayerEventName =
            `game-player-${this.props.gamePlayerData.seat}-event`;
    }

    componentDidMount() {
        window.addEventListener(this.gamePlayerEventName, this.onSeatTick);
        // CC_INLINE_ENDTURN_STALE_ACTIVE_PLAYER (prod G181): the inline
        // End-Turn gate depends on active_player, a GAME-level field the
        // per-seat channel never carries (Game.gamePlayerEvent only swaps
        // one gameplayer_set entry). Subscribe to the game-level channel
        // too so a turn rotation repaints the gate even when no per-seat
        // tick follows — a robots-only turn produced no such tick and
        // stranded the seat with no End Turn until reload.
        window.addEventListener('game-event', this.onSeatTick);
    }

    componentWillUnmount() {
        window.removeEventListener(this.gamePlayerEventName, this.onSeatTick);
        window.removeEventListener('game-event', this.onSeatTick);
    }

    onSeatTick = () => {
        // Force a re-render on every per-seat event. The earlier
        // implementation gated this on action_hex_position equality
        // and used a narrow shouldComponentUpdate, which silently
        // dropped updates to other grid-relevant fields
        // (available_actions, locked_action_stacks, station,
        // action_tokens). That stale grid was the action-tap-fires-
        // wrong-action symptom.
        this.setState((s) => ({ tick: s.tick + 1 }));
    };

    effectiveGamePlayerData = () => {
        // Read from the seated controller for the local seat. The
        // controller's gamePlayerData carries the localIntentPosition
        // override (see GamePlayerController.updateGamePlayerData),
        // so it stays correct during the WS round-trip after a local
        // rotate. Fall back to the React prop for non-local-seat
        // mounts (observer views).
        if (this.seatedController
                && this.props.gamePlayerData.seat === this.seatedController.seat) {
            return this.seatedController.gamePlayerData;
        }
        return this.props.gamePlayerData;
    };

    actionImage = (props) => {
        return <img
            src={getTextureUrl(props.texture)}
            alt="action"
        />;
    };

    onClick = (swiper) => {
        // Read live from the controller, recompute the grid fresh.
        // The render path's grid is a separate, equally-fresh
        // computation — we deliberately do not share it. A stored
        // grid lags the controller by at least one render cycle
        // and would fire the wrong action under fast tap, parent
        // re-render, or WS echo arriving between tap and render.
        const gamePlayerData = this.effectiveGamePlayerData();
        const liveActionGrid = currentActionGrid(gamePlayerData);
        const actionGridIndex = swiper.clickedIndex;

        // 2026-05-22: Guard against blank-area clicks inside the
        // swiper container. Swiper's onClick fires for any click
        // landing on the swiper element, not just on slides. When
        // the user taps padding / whitespace / the alongside End
        // Turn region, clickedIndex is undefined; without this
        // guard, actionRoomTexture below is undefined too,
        // JSON.stringify strips both fields from the package, and
        // the backend receives only {stack, note}. selectActionStack
        // then bombs with "Stack Empty gp:0" because it tries to
        // select from an empty available_actions slot at the stack
        // index derived from whichStacksHaveThisAction(undefined).
        if (actionGridIndex === null
                || actionGridIndex === undefined
                || actionGridIndex < 0
                || actionGridIndex >= liveActionGrid.length) {
            return;
        }
        const actionRoomTexture = liveActionGrid[actionGridIndex];
        if (actionRoomTexture === null
                || actionRoomTexture === undefined) {
            return;
        }

        // 2026-05-16: Guard against firing a selectActionStack /
        // lockAndClickActionGrid click when the seat has already
        // transitioned out of TAKING_TURN_ID (e.g. into
        // ADVERTISING_ID). The "AAA Advertisement Choice Error"
        // backend log was caused by an ActionGrid click leaking
        // into a seat already in ADVERTISING_ID — backend's
        // advertisement_choice expected `data.type` and threw
        // AttributeError. onClick was gated only on turnStatus, but
        // status and turn_status are written by separate engine
        // calls and a transition can leave turn_status at
        // SELECT_ACTION_ID for one cycle after status flipped.
        // GamePlayerPackageSender's allow-list passes ADVERTISING_ID
        // through (it accepts advertisement_choice clicks) so a
        // selectActionStack package would still reach the backend
        // and be misrouted. Block the click at the source.
        const gamePlayerStatus = gamePlayerData.status;
        if (gamePlayerStatus !== GamePlayerStatus.TAKING_TURN_ID &&
            gamePlayerStatus !== GamePlayerStatus.WAITING_ID) {
            console.warn(
                '[ActionSelector] Suppressing action-grid click — seat is in '
                + `status ${gamePlayerStatus} which doesn't accept `
                + 'action-grid clicks (expected TAKING_TURN_ID or WAITING_ID).');
            return;
        }

        const turnStatus = gamePlayerData.turn_status;
        if (turnStatus === TurnStatus.SELECT_ACTION_ID) {
            const actionId = Texture.ACTIONS.indexOf(actionRoomTexture);
            const stackData = whichStacksHaveThisAction(
                gamePlayerData, actionId);
            let stack = null;
            if (actionId < 7) {
                stack = 0;
                if (!stackData.stacks[0]) stack = 1;
            }
            this.api.selectActionStack(actionGridIndex, actionRoomTexture, stack);
        }
        if (turnStatus === TurnStatus.CHOOSE_ACTIONS_ID) {
            const notLocked = !gamePlayerData.action_hex_locked;
            if (!notLocked) {
                console.warn('Choose when locked.');
                return;
            }
            // Main Hex action (textures 71-76): committing to a hex
            // direction inherently locks. Existing Lock&Skip flow.
            if (isMainHexActionTexture(actionRoomTexture)) {
                this.seatedController.lockTheActionHex(
                    gamePlayerData.action_hex_position);
                this.api.lockAndClickActionGrid(actionRoomTexture);
                return;
            }
            // Faction token (textures 59-64) / Random selector (83):
            // Manual ruling per CC_RANDOM_FACTION_NO_HEX_LOCK — do NOT
            // call lockTheActionHex, send a dedicated non-locking click.
            // The backend's finish_action Window-A bypass returns the
            // seat to CHOOSE_ACTIONS_ID so it can rotate, take more
            // bonus actions, or Lock when ready.
            const factionActionId = factionActionIdForTexture(actionRoomTexture);
            if (factionActionId !== null) {
                this.api.takeFactionAction(factionActionId);
                return;
            }
            if (isRandomActionTexture(actionRoomTexture)) {
                this.api.takeRandomAction();
                return;
            }
            // Unknown texture in CHOOSE_ACTIONS_ID — surface, do not silently fire.
            console.warn(
                '[ActionSelector] Unknown action-grid texture in '
                + 'CHOOSE_ACTIONS_ID: ' + actionRoomTexture);
        }
    };

    clickEndTurn = () => {
        if (this.api) this.api.clickEndTurn();
    };

    renderEndTurnContainer(gamePlayerData, extraClass) {
        const variant = VARIANTS[gamePlayerData.faction];
        const containerClass = extraClass
            ? `action-done-container ${extraClass}`
            : 'action-done-container';
        return (
            <div className={containerClass}
                 data-testid={'inline-end-turn-container'}>
                <Button variant={variant}
                        className={'action-done-button'}
                        data-testid={'inline-end-turn-button'}
                        onClick={this.clickEndTurn}>
                    End Turn
                </Button>
                <img src={blank1080} alt={'done-button'}
                     onClick={this.clickEndTurn}
                     className={'big-button-image'}
                />
            </div>
        );
    }

    render() {
        // Render computes its own grid from live state. onClick
        // computes its own grid from live state, independently and
        // freshly. We never hand a grid between them via an instance
        // field — that storage is the cache that lagged the
        // controller and fired the wrong action on a fast tap.
        const gamePlayerData = this.effectiveGamePlayerData();
        const newActionGrid = currentActionGrid(gamePlayerData);
        console.log('[ActionSelector render]',
            'pos:', gamePlayerData.action_hex_position,
            'grid:', newActionGrid);




        // CC_INLINE_ENDTURN_STALE_ACTIVE_PLAYER (prod G181): read
        // active_player from the LIVE authoritative game data, not the
        // stale this.props.gameData snapshot. gamePlayerData above is
        // already read live via effectiveGamePlayerData(); the game-level
        // field must be too, or a robots-only turn (which triggers no
        // fresh prop reflow) leaves active_player lagging at the previous
        // player and the empty-grid branch hides End Turn until reload.
        const liveGameData =
            (this.props.app && this.props.app.game
                && this.props.app.game.gameData)
                ? this.props.app.game.gameData
                : this.props.gameData;
        const showEndTurn = shouldShowInlineEndTurn(
            gamePlayerData, liveGameData);

        // Hex exhausted with no remaining bonus actions: full-region
        // End Turn replaces the swiper, matching the pre-`8b8b747`
        // affordance Michael had before the standalone EndTurnButton
        // was inserted.
        if (newActionGrid.length === 0) {
            if (showEndTurn) {
                return (
                    <div className={'action-scroller'}>
                        <div className={'action-selector-anchor'}>
                            {this.renderEndTurnContainer(gamePlayerData)}
                        </div>
                    </div>
                );
            }
            return <></>;
        }

        // Hex actions remaining: render the swiper. If hex is
        // exhausted but bonus (faction / random) actions are still in
        // the grid, render the End Turn on its own row BELOW the
        // swiper — bank-and-yield override per Manual §7.1. The
        // earlier "alongside" overlay variant placed End Turn inside
        // the swiper's positioning anchor at z-index 2, but the
        // swiper-overlay frame artwork (z-index 10) sits on top of
        // both the slide icons and the alongside container, hiding
        // the lone bonus-action icon visually and creating ambiguous
        // click targets. Below-row keeps both the icons and the
        // End Turn clearly visible and clickable. 2026-05-22.
        return (
            <div className={'action-scroller'}>
                <div className={'action-selector-anchor'}>
                    <div className={'action-selector-swiper-container'}>
                        <Swiper
                            slidesPerView={6}
                            spaceBetween={0}
                            grabCursor={true}
                            onSwiper={(swiper) => { this.swiper = swiper; }}
                            onClick={this.onClick}
                            className="action-scroller-swiper"
                        >
                            {newActionGrid.map((gridItem, index) => {
                                return <SwiperSlide key={`gi-${index}`}
                                                    virtualIndex={index}>
                                    <this.actionImage texture={gridItem}/>
                                </SwiperSlide>;
                            })}
                        </Swiper>
                    </div>
                    <img
                        src={`${ASSETS_URL}overlays/ActionSelectorOverlay-1080x210.png`}
                        alt="action"
                        className={'swiper-overlay'}
                    />
                </div>
                {showEndTurn && this.renderEndTurnContainer(
                    gamePlayerData, 'action-done-container--below')}
            </div>
        );
    }
}
